/**
 * Mounts the pilot body (PilotBody) and the navbar (PilotNavbar).
 * The DocsToOutcomes hero illustration is rendered via createPortal
 * into `#pp-hero-illu-mount` (inside Story scene 1's grid).
 *
 * Pilot is a thin shell — PilotProvider lives one level up in
 * PilotLandingPage so the about / bottom-cta / footer siblings can
 * read viewMode for their own visibility.
 *
 * Engine contract — DOM hooks `lib/pilot/pilot-engine.js`,
 * `lib/pilot/modals.js`, `lib/pilot/claude.ts`, `lib/pilot/demo.js`,
 * and `lib/pilot/vision.js` bind against:
 *   #nav-story / #nav-sim / #nav-pitch / #nav-demo / #nav-vision
 *   #topbar-talk
 *   #api-key-input / #generate-btn / #modal-cards / #modal-close /
 *   #modal-error
 *   #nb-* (notebook), #story-* (story), #pitch-* (pitch),
 *   #vis-* (vision)
 *   #demo (empty section; demo.js paints into it)
 *   .pilot-mount  (engine root)
 *   data-tab / data-pane / data-action / data-tt
 */

import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PilotBody from './PilotBody';
import PilotNavbar from './PilotNavbar';
import CoordinationIllustration from './CoordinationIllustration';
import Pitch from './Pitch';
import Story from './Story';
import Demo from './Demo';
import Vision from './Vision';
import {usePilot, ViewMode} from './PilotContext';
import {PILOT_SET_VIEW_MODE_EVENT} from '../../lib/pilot/events';

interface PilotProps {
  bridgeInstalled: boolean;
  /** Surfaced from PilotLandingPage when installPilotBridge throws. */
  bridgeError?: string | null;
}

export default function Pilot({bridgeInstalled, bridgeError = null}: PilotProps) {
  // F17 Slice D: PilotProvider moved UP to PilotLandingPage so
  // sibling components (PilotAbout, PilotBottomCTA, PilotFooter)
  // can read viewMode to drive their own visibility instead of the
  // body.X-mode CSS gates this slice retired. Pilot is now a plain
  // PilotInner wrapper; PilotProvider's mount stays single-rooted at
  // PilotLandingPage.
  return <PilotInner bridgeInstalled={bridgeInstalled} bridgeError={bridgeError} />;
}

function PilotInner({bridgeInstalled, bridgeError = null}: PilotProps) {
  const mountedRef = useRef(false);
  const [heroIlluTarget, setHeroIlluTarget] = useState<Element | null>(null);
  const [engineLoadFailed, setEngineLoadFailed] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const {viewMode, setViewMode} = usePilot();
  const lastViewModeRef = useRef<ViewMode | null>(null);

  // Imperative engine code (lib/pilot/*.js) requests view-mode
  // transitions by dispatching a `pilot:set-view-mode` CustomEvent.
  // This is the bridge: React listens, calls setViewMode, the
  // PilotProvider effect applies the body class. Phase 1 of the
  // port — Phases 4-7 progressively delete the engine code that
  // dispatches these, and the bridge can come out at the end.
  useEffect(() => {
    function onRequest(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (
        detail === 'story' ||
        detail === 'sandbox' ||
        detail === 'pitch' ||
        detail === 'demo' ||
        detail === 'vision'
      ) {
        setViewMode(detail);
      }
    }
    window.addEventListener(PILOT_SET_VIEW_MODE_EVENT, onRequest);
    return () => window.removeEventListener(PILOT_SET_VIEW_MODE_EVENT, onRequest);
  }, [setViewMode]);

  // Find the in-body illustration mount node once PilotBody has rendered.
  // Runs once on mount; the ref+state guard makes it idempotent so an
  // accidental re-run wouldn't break anything either.
  useEffect(() => {
    if (heroIlluTarget || !rootRef.current) return;
    const node = rootRef.current.querySelector('#pp-hero-illu-mount');
    if (node) setHeroIlluTarget(node);
  }, [heroIlluTarget]);

  useEffect(() => {
    if (!bridgeInstalled || mountedRef.current) return;
    mountedRef.current = true;
    // The pilot-engine.js boot module wires the surviving imperative
    // glue (modal flows, sandbox scroll-spy, sandbox-side dots,
    // intake "clear filter", story mp:unlock handler, scroll-restore).
    // Every paint surface that was previously imperative is now React-
    // owned in Sandbox.tsx / Story.tsx — see
    // `docs/eng/pilot-engine-imperative-retirement.md` Phase L.
    import('../../lib/pilot/pilot-engine.js').catch((err) => {
      console.error('[pilot] Failed to load pilot-engine.js:', err);
      // Without pilot-engine.js the user gets a /pilot shell with no
      // modals, sandbox scroll-spy, or unlock — surface it so they can
      // reload instead of staring at a silent half-page.
      setEngineLoadFailed(true);
    });

    return () => {
      if (typeof document !== 'undefined') {
        // Clean up *non* view-mode flags that pilot lifecycle code
        // sets. View-mode classes are owned by PilotProvider's effect.
        document.body.classList.remove('report-locked', 'tldr-pinned');
        document.documentElement.removeAttribute('data-theme');
      }
    };
  }, [bridgeInstalled]);

  // Page-scoped side effects: favicon scrub on /#/pilot and the
  // `pp-scrolled-past-hero` body flag that fades the hero side-accent
  // illustration once the visitor scrolls past scene 1. Both were
  // previously in PilotLandingPage, where they fired even during the
  // invite-gate (no hero in the DOM to react to). Scoping here means
  // they mount/unmount with the post-gate Pilot tree.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const iconLinks = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>(
        'link[rel="icon"], link[rel="apple-touch-icon"], link[rel="shortcut icon"]',
      ),
    );
    const originalHrefs = iconLinks.map((l) => l.href);
    const TRANSPARENT_PIXEL =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    iconLinks.forEach((l) => {
      l.href = TRANSPARENT_PIXEL;
    });

    const onScroll = () => {
      const scrolled = window.scrollY > 300;
      document.body.classList.toggle('pp-scrolled-past-hero', scrolled);
    };
    window.addEventListener('scroll', onScroll, {passive: true});

    return () => {
      iconLinks.forEach((l, i) => {
        l.href = originalHrefs[i];
      });
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('pp-scrolled-past-hero');
    };
  }, []);

  // Bridge React's viewMode transitions into scroll plumbing for the
  // Story+Sandbox shared scroll. Pitch / Demo / Vision lifecycles
  // are component-owned: <Pitch /> + <Demo /> + <Vision /> mount on
  // viewMode and tear down on unmount, so this effect just handles
  // the in-mode scroll target.
  useEffect(() => {
    if (!bridgeInstalled) return;
    const prev = lastViewModeRef.current;
    if (prev === viewMode) return;

    // Story/Sandbox share scroll; just scroll into the right region.
    // Takeover teardown (pitch/demo/vision) is handled by their
    // React unmount cleanup.
    if (viewMode === 'sandbox') {
      // Sandbox is an additive scroll on top of Story. Scroll into
      // the notebook so the visitor lands at the simulator.
      const target = document.getElementById('notebook');
      if (target) {
        target.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    } else if (viewMode === 'story') {
      // Returning to Story from a takeover — reset scroll. From
      // Sandbox, do nothing (the visitor's scroll position is the
      // useful one).
      if (prev !== 'sandbox') {
        document.documentElement.scrollTo({top: 0, behavior: 'smooth'});
      }
    }

    lastViewModeRef.current = viewMode;
  }, [viewMode, bridgeInstalled]);

  // If either bridge (the Apollo lead-capture bridge installed in
  // PilotLandingPage, or the imperative engine dynamically imported
  // above) failed, the page is non-functional. Render a banner above
  // the rest of the tree so the visitor knows to reload rather than
  // staring at a silent shell where buttons don't respond. We still
  // render the rest of the tree underneath — partial functionality
  // (e.g. Story scroll without modals) is more useful than a blank
  // page, and the banner makes the degradation visible.
  const bannerMessage =
    bridgeError != null
      ? `Pilot couldn't initialize (${bridgeError}). Some interactive features may not work. Please refresh.`
      : engineLoadFailed
      ? "Pilot couldn't load its interactive engine. Modals, sliders, and lead-capture won't work. Please refresh."
      : null;

  return (
    <div className="pp-pilot-root oatmeal-root" ref={rootRef}>
      {bannerMessage && (
        <div
          role="alert"
          aria-live="assertive"
          className="pp-bridge-banner sticky top-0 z-50 flex items-center justify-between gap-4 bg-red-600 px-4 py-2 text-sm/6 text-white"
        >
          <span>{bannerMessage}</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center rounded-md bg-white/10 px-3 py-1 text-sm/5 font-medium hover:bg-white/20"
          >
            Reload
          </button>
        </div>
      )}
      <PilotNavbar />
      <div className="pilot-mount">
        {/* Story + Sandbox are always mounted (their `hidden` prop
            tracks viewMode so DOM + scroll position survive a view
            switch). story-controls + story-dots are still gated by
            the `body:not(.story-mode)` rule in pilot-engine.css. */}
        <Story />
        <PilotBody />
      </div>
      {/* View takeovers — conditionally mounted by viewMode. Each owns
          its own DOM tree (deck/canvas + chrome) and tears down its
          listeners on unmount. */}
      {viewMode === 'pitch' && <Pitch />}
      {viewMode === 'demo' && <Demo />}
      {viewMode === 'vision' && <Vision />}
      {heroIlluTarget &&
        createPortal(
          <CoordinationIllustration size={440} className="pp-hero-illu" />,
          heroIlluTarget,
        )}
    </div>
  );
}
